/**
 * Banners from API — main screen hero background and filtered lists.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  bannersQueryKey,
  fetchBanners,
  getBannerImageUrl,
  pickMainHeroBanner,
  resolveBannerHeroMedia,
  type Banner,
  type BannerHeroMedia,
} from './bannersApi';

export type { Banner, BannerHeroMedia };
export { bannersQueryKey };

export type BannerFilterKind = 'pop' | 'banner' | 'all';

function getBannerTitleLower(banner: Banner): string {
  const t = banner?.title ?? '';
  const at = banner?.arabic_title ?? '';
  return `${typeof t === 'string' ? t : ''} ${typeof at === 'string' ? at : ''}`.toLowerCase();
}

const BANNER_FILTER_PREDICATES: Record<BannerFilterKind, (banner: Banner) => boolean> = {
  all: () => true,
  pop: banner => getBannerTitleLower(banner).includes('pop'),
  banner: banner => getBannerTitleLower(banner).includes('banner'),
};

export function useBanners(filter: BannerFilterKind = 'all') {
  const query = useQuery({
    queryKey: bannersQueryKey,
    queryFn: fetchBanners,
    staleTime: 1000 * 60 * 10,
    enabled: true,
  });
  const raw_data = Array.isArray(query.data) ? query.data : [];
  const data = useMemo(() => {
    const predicate = BANNER_FILTER_PREDICATES[filter] ?? BANNER_FILTER_PREDICATES.all;
    return raw_data.filter(predicate);
  }, [raw_data, filter]);
  const hero_banner = useMemo(() => pickMainHeroBanner(data), [data]);
  const hero_media = useMemo(
    () => (hero_banner ? resolveBannerHeroMedia(hero_banner) : null),
    [hero_banner],
  );
  const backgroundImageUri = useMemo(
    () => (hero_banner ? getBannerImageUrl(hero_banner) : null),
    [hero_banner],
  );

  return {
    data,
    isLoading: query.isLoading,
    error: query.error instanceof Error ? query.error : null,
    refetch: query.refetch,
    /** Still image only (video/YouTube primaries without image fallback → null). */
    backgroundImageUri,
    /** Image / video file / YouTube — prefer `url`, fall back to `attachment`. */
    heroMedia: hero_media,
    heroBanner: hero_banner,
    mainHeroBannerCopySuppressed: raw_data.length > 1 && data.length > 1,
  };
}
